import { useI18n } from '../../../i18n';
// ============================================
// RegionalHeatmap Component
// Placeholder for regional volume heatmap visualization
// ============================================

import { cn } from '../../../utils/cn';

// ============================================
// Types
// ============================================

interface RegionalHeatmapProps {
  /** Optional title override */
  title?: string;
  /** Loading state */
  isLoading?: boolean;
  /** Additional className */
  className?: string;
}

// ============================================
// Component
// ============================================

export function RegionalHeatmap({
  title,
  isLoading,
  className,
}: RegionalHeatmapProps) {
  const { t } = useI18n();
  const displayTitle = title || t('regionalVolumeHeatmap');
  if (isLoading) {
    return (
      <div
        className={cn(
          'flex h-full min-h-[280px] animate-pulse flex-col rounded-xl bg-[#E7E7F3] p-6',
          className
        )}
      >
        <div className="mb-4 h-6 w-48 rounded bg-slate-300/50" />
        <div className="flex-1 rounded bg-slate-300/30" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex h-full flex-col rounded-xl bg-[#E7E7F3] p-6',
        className
      )}
    >
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-bold text-slate-900">{displayTitle}</h3>
        
        {/* Legend */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{t('low')}</span>
          <div className="flex h-2 w-24 overflow-hidden rounded">
            <div className="flex-1 bg-blue-100" />
            <div className="flex-1 bg-blue-200" />
            <div className="flex-1 bg-blue-300" />
            <div className="flex-1 bg-blue-400" />
            <div className="flex-1 bg-blue-500" />
            <div className="flex-1 bg-blue-600" />
          </div>
          <span className="text-xs text-slate-500">{t('high')}</span>
        </div>
      </div>

      {/* Placeholder Map */}
      <div className="relative flex flex-1 items-center justify-center rounded-lg bg-slate-200/50">
        {/* Simulated heatmap regions */}
        <svg
          viewBox="0 0 400 200"
          className="h-full w-full max-w-[400px]"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Background */}
          <rect
            x="0"
            y="0"
            width="400"
            height="200"
            fill="#E7E7F3"
            rx="8"
          />
          
          {/* Placeholder regions with varying intensities */}
          <ellipse cx="80" cy="60" rx="35" ry="25" fill="#93C5FD" opacity="0.7" />
          <ellipse cx="150" cy="100" rx="45" ry="30" fill="#3B82F6" opacity="0.8" />
          <ellipse cx="250" cy="70" rx="40" ry="28" fill="#60A5FA" opacity="0.6" />
          <ellipse cx="320" cy="120" rx="50" ry="35" fill="#2563EB" opacity="0.9" />
          <ellipse cx="200" cy="150" rx="55" ry="30" fill="#1D4ED8" opacity="0.85" />
          <ellipse cx="100" cy="140" rx="30" ry="20" fill="#BFDBFE" opacity="0.5" />
          
          {/* Grid lines */}
          <g stroke="#CBD5E1" strokeWidth="0.5" opacity="0.3">
            <line x1="0" y1="50" x2="400" y2="50" />
            <line x1="0" y1="100" x2="400" y2="100" />
            <line x1="0" y1="150" x2="400" y2="150" />
            <line x1="100" y1="0" x2="100" y2="200" />
            <line x1="200" y1="0" x2="200" y2="200" />
            <line x1="300" y1="0" x2="300" y2="200" />
          </g>
        </svg>

        {/* TODO: Implement actual heatmap with mapping library (Mapbox/D3) */}
        <div className="absolute bottom-2 right-2 rounded bg-slate-800/60 px-2 py-1 text-[10px] text-white">
          {t('heatmapVisualizationPlaceholder')}
        </div>
      </div>
    </div>
  );
}
